import React from 'react';
import { Stack, Alert, Tooltip, MenuItem, CircularProgress } from '@mui/material';
import Save from '@mui/icons-material/Save';
import { StyledMenu } from 'common/utils/common.util';
import Footer from '../../components/general/Footer';
import { Button, Typography } from 'components/index';
import { UserDTO } from '../../components/user-management/auth.type';
import { ReportTypes } from 'common/utils/util.type';
import { KeyboardArrowUp } from '@mui/icons-material';
import { PermissionGate } from '../common/PermissionGate';

interface CCReportFooterProps {
  report: any;
  isReportDirty: boolean;
  isEditMode: boolean;
  isDeleting: boolean;
  isReportLoading: boolean;
  anchorEl: null | HTMLElement;
  open: boolean;
  joinDateAndUser: (date: string, user: UserDTO, prefix: string) => string;
  handleDelete: () => void;
  saveReport: () => void;
  handleCreateCopy?: () => void;
  duplicateReport?: () => void;
  handleClick: (event: React.MouseEvent<HTMLElement>) => void;
  handleClose: () => void;
  handleOpenCompsDialog: () => void;
  handleExportAsWord: () => void;
  onExportInvoice?: () => void;
  isExporting?: boolean;
  typeofReport?: ReportTypes;
}

const CCReportFooter: React.FC<CCReportFooterProps> = ({
  report,
  isReportDirty,
  isEditMode,
  isDeleting,
  isReportLoading,
  anchorEl,
  open,
  joinDateAndUser,
  handleDelete,
  saveReport,
  duplicateReport,
  handleCreateCopy,
  handleClick,
  handleClose,
  handleOpenCompsDialog,
  handleExportAsWord,
  isExporting,
  typeofReport,
  onExportInvoice,
}) => {
  return (
    <Footer>
      <Stack direction={'row'} justifyContent={'space-between'} alignItems={'center'} flex={1}>
        <Stack direction={'row'} spacing={1}>
          <Typography variant='subtitle2'>{joinDateAndUser(report.createdOn, report.createdBy, 'Generated on ')}</Typography>
        </Stack>
        <Stack direction={'row'} spacing={1}>
          {isReportDirty ? <Alert severity='warning'>Report has unsaved changes</Alert> : null}
        </Stack>
        <Stack direction={'row'} spacing={1}>
          <Button
            startIcon={isExporting ? <CircularProgress size={20} color='inherit' /> : <KeyboardArrowUp />}
            variant='outlined'
            onClick={handleClick}
            disabled={isExporting}
          >
            Actions
          </Button>
          <Button startIcon={<Save />} onClick={saveReport} loading={isReportLoading} disabled={!isReportDirty}>
            Save
          </Button>
          <StyledMenu
            id='long-menu'
            MenuListProps={{
              'aria-labelledby': 'long-button',
            }}
            anchorEl={anchorEl}
            open={open}
            onClose={handleClose}
            slotProps={{
              paper: {
                sx: {
                  maxHeight: 48 * 4.5,
                  width: '240px',
                  background: 'transparent !important',
                  boxShadow: 'none !important',
                  overflow: 'visible',
                },
              },
            }}
            anchorOrigin={{
              vertical: 'top',
              horizontal: -20,
            }}
            transformOrigin={{
              vertical: 'bottom',
              horizontal: 'left',
            }}
          >
            {typeofReport === ReportTypes.CC ? (
              <PermissionGate actionRole={'DELETE_CC_REPORT'}>
                <Tooltip title={'Delete Report'} arrow={false}>
                  <MenuItem onClick={handleDelete} disabled={isEditMode}>
                    DELETE REPORT
                  </MenuItem>
                </Tooltip>
              </PermissionGate>
            ) : (
              <PermissionGate actionRole={'DELETE_QCC_REPORT'}>
                <Tooltip title={'Delete Report'} arrow={false}>
                  <MenuItem onClick={handleDelete} disabled={isEditMode}>
                    DELETE REPORT
                  </MenuItem>
                </Tooltip>
              </PermissionGate>
            )}
            <Tooltip title={'Add Comparables to Report'} arrow={false}>
              <MenuItem
                onClick={() => {
                  handleOpenCompsDialog();
                  handleClose();
                }}
              >
                ADD COMPARABLES
              </MenuItem>
            </Tooltip>
            {typeofReport === ReportTypes.CC && handleCreateCopy && (
              <>
                <Tooltip title={'Duplicate Report'} arrow={false}>
                  <MenuItem
                    onClick={() => {
                      handleCreateCopy();
                      handleClose();
                    }}
                    disabled={isReportLoading}
                  >
                    DUPLICATE
                  </MenuItem>
                </Tooltip>
                <Tooltip title={'Export Report as Word file'} arrow={false}>
                  <MenuItem onClick={handleExportAsWord} disabled={isExporting}>
                    EXPORT AS WORD
                  </MenuItem>
                </Tooltip>
                {onExportInvoice && (
                  <Tooltip title={'Export Invoice'} arrow={false}>
                    <MenuItem onClick={onExportInvoice} disabled={isExporting}>
                      EXPORT INVOICE
                    </MenuItem>
                  </Tooltip>
                )}
              </>
            )}
          </StyledMenu>
        </Stack>
      </Stack>
    </Footer>
  );
};
export default CCReportFooter;
